/**
 * The keymap and the commands the toolbar drives (TASK-050).
 *
 * Deliberately small. Euan: "We're not going to go to town with this. We only
 * want a certain set of things for humans to at least just modify along with
 * agents." So: the five toggleable marks, links, undo/redo, and the input
 * rules that make typing feel like typing. Everything structural -- splitting
 * a paragraph, making a list, joining blocks -- is the next slice, and lives
 * outside this file because it produces OPS, not transactions.
 */
import {
  baseKeymap,
  chainCommands,
  exitCode,
  lift,
  setBlockType,
  toggleMark,
  wrapIn,
} from 'prosemirror-commands'
import { liftListItem, sinkListItem, splitListItem, wrapInList } from 'prosemirror-schema-list'
import { history, undo, redo } from 'prosemirror-history'
import { keymap } from 'prosemirror-keymap'
import { inputRules, smartQuotes } from 'prosemirror-inputrules'
import type { Command, EditorState } from 'prosemirror-state'
import { blockSchema, type Toggleable } from './schema.ts'

/** Is this mark on, at the cursor or across the selection? */
export function markActive(state: EditorState, name: string): boolean {
  const type = blockSchema.marks[name]
  if (!type) return false
  const { from, $from, to, empty } = state.selection
  return empty
    ? !!type.isInSet(state.storedMarks || $from.marks())
    : state.doc.rangeHasMark(from, to, type)
}

export function toggle(name: Toggleable): Command {
  const type = blockSchema.marks[name]
  return type ? toggleMark(type) : () => false
}

/**
 * Put a link on the selection, or take one off.
 *
 * `href` is not validated here. The server's `safeHref` is the one that
 * decides what a link may point at, and it runs on every read -- a second
 * opinion in the browser would be a second rule to keep in step, and the
 * browser's would be the one nobody could rely on.
 */
export function setLink(href: string | null): Command {
  const type = blockSchema.marks.link
  if (!type) return () => false
  return (state, dispatch) => {
    const { from, to, empty } = state.selection
    if (empty) return false
    if (!dispatch) return true
    const tr = state.tr
    if (href) tr.addMark(from, to, type.create({ href }))
    else tr.removeMark(from, to, type)
    dispatch(tr)
    return true
  }
}

/* ---------------------------------------------------------------- blocks */

/**
 * Make the blocks in the selection headings, or body text again.
 *
 * NOT `setBlockType`, which replaces a node's attributes wholesale -- and this
 * system's attributes are its identity. `id` is what comment threads point at
 * and what the op vocabulary targets; `h` is what the write guard compares.
 * Dropping them turns "this paragraph is now a heading" into "this paragraph
 * was deleted and an unrelated one appeared", which detaches every thread on
 * it and loses the guard.
 *
 * So the node is re-marked in place, keeping everything it was carrying and
 * changing only the one attribute that is being changed.
 */
export function setHeading(level: number | null): Command {
  const paragraph = blockSchema.nodes.paragraph
  return (state, dispatch) => {
    if (!paragraph) return false
    const { from, to } = state.selection
    let found = false
    const tr = state.tr
    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type !== paragraph) return
      found = true
      if (dispatch) tr.setNodeMarkup(pos, paragraph, { ...node.attrs, heading: level })
    })
    if (found && dispatch) dispatch(tr)
    return found
  }
}

/** Which heading level the cursor is in, or null for body text. */
export function headingAt(state: EditorState): number | null {
  const at = state.selection.$from
  for (let d = at.depth; d >= 0; d--) {
    const node = at.node(d)
    if (node.type === blockSchema.nodes.paragraph) {
      const level = node.attrs.heading
      return typeof level === 'number' ? level : null
    }
  }
  return null
}

/**
 * Wrap the selection in a list of this kind.
 *
 * NOTE, and it is a real one: a paragraph becoming a list item is a
 * STRUCTURAL change -- the paragraph moves inside a container that did not
 * exist -- and the server mints ids for everything in an insert (BUG-012), so
 * the paragraphs come back with new ones. Comment threads on them detach, and
 * the editor asks before sending anything that would. Turning a heading into
 * body text, by contrast, keeps every id, which is why that path is written
 * the way it is above.
 */
export function makeList(kind: 'bullet' | 'ordered' | 'check'): Command {
  const list = blockSchema.nodes.list
  return list ? wrapInList(list, { kind }) : () => false
}

/** A fenced code block, keeping the block's identity. */
export function makeCodeBlock(): Command {
  const code = blockSchema.nodes.codeBlock
  return (state, dispatch) => {
    if (!code) return false
    const at = state.selection.$from
    const node = at.node(at.depth)
    if (node.type === code) return false
    return setBlockType(code, { ...node.attrs, language: null })(state, dispatch)
  }
}

/** A divider, inserted where the cursor is. */
export function insertRule(): Command {
  const rule = blockSchema.nodes.horizontalRule
  return (state, dispatch) => {
    if (!rule) return false
    if (dispatch) dispatch(state.tr.replaceSelectionWith(rule.create()).scrollIntoView())
    return true
  }
}

/** Wrap in, or lift out of, a blockquote. */
export function toggleQuote(): Command {
  const quote = blockSchema.nodes.blockquote
  return (state, dispatch) => {
    if (!quote) return false
    const at = state.selection.$from
    for (let d = at.depth; d > 0; d--) {
      if (at.node(d).type === quote) return lift(state, dispatch)
    }
    return wrapIn(quote)(state, dispatch)
  }
}

/**
 * The keys.
 *
 * Mod- is Cmd on a Mac and Ctrl elsewhere, which prosemirror-keymap resolves.
 * Bold/italic/underline are the three every editor in the world binds and a
 * person will try without being told. Strike and code get the bindings Google
 * Docs and Notion use, which is the nearest thing to a convention.
 */
export function editorKeymap(onEscape: () => void) {
  return keymap({
    'Mod-b': toggle('bold'),
    'Mod-i': toggle('italic'),
    'Mod-u': toggle('underline'),
    'Mod-Shift-x': toggle('strike'),
    'Mod-e': toggle('code'),
    'Mod-z': undo,
    'Mod-y': redo,
    'Mod-Shift-z': redo,
    /*
     * Enter inside a list splits the ITEM; everywhere else `baseKeymap` splits
     * the block, which is what makes a new paragraph. Both produce a block
     * with no id, which is exactly how the diff recognises something new.
     */
    Enter: splitListItem(blockSchema.nodes.listItem!),
    'Shift-Enter': chainCommands(exitCode, (state, dispatch) => {
      if (dispatch) dispatch(state.tr.insertText('\n').scrollIntoView())
      return true
    }),
    Tab: sinkListItem(blockSchema.nodes.listItem!),
    'Shift-Tab': liftListItem(blockSchema.nodes.listItem!),
    'Mod-Alt-0': setHeading(null),
    'Mod-Alt-1': setHeading(1),
    'Mod-Alt-2': setHeading(2),
    'Mod-Alt-3': setHeading(3),
    Escape: () => {
      onEscape()
      return true
    },
  })
}

export function editorPlugins(onEscape: () => void) {
  return [
    editorKeymap(onEscape),
    keymap(baseKeymap),
    history(),
    // Curly quotes and an em dash: the typography the read view already shows,
    // so what is typed matches what an agent's Markdown produces.
    inputRules({ rules: [...smartQuotes] }),
  ]
}
